import { useCallback } from 'react'
import { BigNumber, ethers } from 'ethers'
import { useMproContract } from 'hooks/useContract'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useSwitchChainForContractTx } from 'hooks/useSwitchChainForContractTx'
import { SendParamStruct } from 'config/abi/types/Mpro'
import { Options } from '@layerzerolabs/lz-v2-utilities'
import { LZ_CHAIN_ID } from 'config/constants/lzChainId'

export const useMproTokenContract = () => {
  const { account, provider, chainId } = useActiveWeb3React()
  const getMproContract = useMproContract()
  const ensureChainForTx = useSwitchChainForContractTx()

  const balance = useCallback(
    async ({ chainId }: { chainId: number }) => {
      if (!account) {
        return ethers.constants.Zero
      }

      const TokenContract = getMproContract(chainId)
      const balanceOf = await TokenContract.balanceOf(account)

      return balanceOf
    },
    [account, getMproContract]
  )

  const estimateSendFee = useCallback(
    async ({
      sourceChainId,
      destinationChainId,
      toAddress,
      amount
    }: {
      sourceChainId: number
      destinationChainId: number
      toAddress: string
      amount: BigNumber
    }) => {
      if (!ethers.utils.isAddress(toAddress)) {
        throw new Error('Invalid destination address')
      }

      const TokenContract = getMproContract(sourceChainId)
      const lzChainId = LZ_CHAIN_ID[destinationChainId]
      const zeroPadReceiver = ethers.utils.zeroPad(toAddress, 32)

      const createSendParams = (chainId: number, quantity: BigNumber): SendParamStruct => {
        const options = Options.newOptions().addExecutorLzReceiveOption(200000, 0).toHex().toString()

        const sendParam = [
          chainId,
          zeroPadReceiver,
          quantity.toBigInt(),
          quantity.toBigInt(),
          options,
          '0x',
          '0x'
        ] as unknown as SendParamStruct
        return sendParam
      }

      const sendParam = createSendParams(lzChainId, amount)
      const nativeFee = await TokenContract.quoteSend(sendParam, false)

      return nativeFee[0]
    },

    [getMproContract]
  )

  const sendFrom = useCallback(
    async ({
      sourceChainId,
      destinationChainId,
      toAddress,
      amount
    }: {
      sourceChainId: number
      destinationChainId: number
      toAddress: string
      amount: BigNumber
    }) => {
      if (!provider) {
        throw new Error('Wallet provider unavailable')
      }
      if (!ethers.utils.isAddress(toAddress)) {
        throw new Error('Invalid destination address')
      }

      const onSourceChain = await ensureChainForTx(sourceChainId)
      if (!onSourceChain) {
        throw new Error('Network switch required')
      }
      const TokenContract = getMproContract(sourceChainId)
      const lzChainId = LZ_CHAIN_ID[destinationChainId]
      const zeroPadReceiver = ethers.utils.zeroPad(toAddress, 32)
      const zero = ethers.constants.Zero

      const createSendParams = (chainId: number, quantity: BigNumber): SendParamStruct => {
        const options = Options.newOptions().addExecutorLzReceiveOption(200000, 0).toHex().toString()

        const sendParam = [
          chainId,
          zeroPadReceiver,
          quantity.toBigInt(),
          quantity.toBigInt(),
          options,
          '0x',
          '0x'
        ] as unknown as SendParamStruct
        return sendParam
      }
      const sendParam = createSendParams(lzChainId, amount)

      const nativeFee = await TokenContract.quoteSend(sendParam, false)
      const signer = provider.getSigner()

      const sendTransaction = await TokenContract.connect(signer).send(
        sendParam,
        [nativeFee[0], zero] as any,
        account,
        { value: nativeFee[0] }
      )
      return sendTransaction
    },
    [account, provider, ensureChainForTx, getMproContract]
  )

  return { balance, estimateSendFee, sendFrom }
}
